import logoAsset from "@/assets/OP2P-CRM-e-Agentes-de-IA-logotipo-branco-2026.webp.asset.json";
import logoColorAsset from "@/assets/OP2P-CRM-e-Agentes-de-IA-logotipo-colorido-2026.webp.asset.json";
import { Mail, Phone, Linkedin, Instagram, Youtube, Facebook, CalendarDays } from "lucide-react";
import { WHATSAPP_TRIAL_LINK, WHATSAPP_SUPPORT_LINK } from "@/components/FloatingButtons";
import { useTheme } from "@/hooks/use-theme";
import { Link } from "@/lib/router-compat";
import { HashLink } from "@/components/HashLink";
import { PreferredSourceButton } from "@/components/PreferredSourceButton";
import { integrationFooterLinks } from "@/lib/integration-pages";

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.6 5.82s.51.5 0 0A4.278 4.278 0 0 1 15.54 3h-3.09v12.4a2.592 2.592 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6 0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64 0 3.33 2.76 5.7 5.69 5.7 3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.24-1.48z" />
  </svg>
);

const footerLinks = {
  navigation: [
    { name: "Início", href: "/#inicio" },
    { name: "Como funciona", href: "/#como-funciona" },
    { name: "Soluções", href: "/#solucoes" },
    { name: "Migre seu CRM", href: "/#migre" },
    { name: "Benefícios", href: "/#beneficios" },
    { name: "Briefing de IA", href: "/briefing" },
  ],
  solutions: [
    { name: "Planos", href: "/precos" },
    { name: "Integrações", href: "/integracoes" },
    { name: "CRM com WhatsApp e Instagram", href: "/crm-com-whatsapp" },
    { name: "Agentes de IA nos canais", href: "/agente-de-ia-para-whatsapp" },
    { name: "Chatbot ou Agente de IA?", href: "/chatbot-vs-agente-de-ia" },
    { name: "POC de 7 dias", href: WHATSAPP_TRIAL_LINK, external: true },
    { name: "Agende uma reunião", href: "/agenda" },
    { name: "Suporte via WhatsApp", href: WHATSAPP_SUPPORT_LINK, external: true },
  ],
  segments: [
    { name: "Clínicas e consultórios", href: "/agente-ia-clinica" },
    { name: "Odontologia", href: "/agente-ia-odontologia" },
    { name: "Farmácia", href: "/agente-ia-farmacia" },
    { name: "Restaurante e delivery", href: "/agente-ia-restaurante" },
    { name: "Imobiliária", href: "/agente-ia-imobiliaria" },
    { name: "Barbearia e salão", href: "/agente-ia-barbearia" },
    { name: "Academia", href: "/agente-ia-academia" },
    { name: "Pet shop", href: "/agente-ia-petshop" },
    { name: "Hotel e pousada", href: "/agente-ia-hotel" },
    { name: "E-commerce", href: "/agente-ia-ecommerce" },
    { name: "Oficina mecânica", href: "/agente-ia-oficina-mecanica" },
    { name: "Concessionária e loja de veículos", href: "/agente-ia-concessionaria-loja-de-veiculos" },
    { name: "Loteadora e incorporadora", href: "/agente-ia-loteadora-incorporadora" },
    { name: "Escola e cursos", href: "/agente-ia-escola-curso" },
    { name: "Contabilidade", href: "/agente-ia-contabilidade" },
    { name: "Advocacia", href: "/agente-ia-advocacia" },
    { name: "Corretora de seguros", href: "/agente-ia-seguros" },
    { name: "CRM para corretora de seguros", href: "/crm-para-corretora-de-seguros" },
    { name: "Por que POC e setup", href: "/por-que-poc-e-setup" },
    { name: "Plano de saúde", href: "/agente-ia-plano-de-saude" },
    { name: "Seguro de vida", href: "/agente-ia-seguro-de-vida" },
    { name: "Seguro auto", href: "/agente-ia-seguro-auto" },
    { name: "Seguradora", href: "/agente-ia-seguradora" },
  ],
  company: [
    { name: "Sobre nós", href: "/sobre" },
    { name: "Blog", href: "/blog" },
    { name: "Política de Privacidade", href: "/privacidade" },
    { name: "Termos de Uso", href: "/termos-de-uso" },
    { name: "Política de Cookies", href: "/cookies" },
  ],
};

const socialLinks = [
  { icon: Instagram, href: "https://www.instagram.com/op2p.ai", label: "Instagram" },
  { icon: Facebook, href: "https://www.facebook.com/op2pai", label: "Facebook" },
  { icon: TikTokIcon, href: "https://www.tiktok.com/@op2p.ai", label: "TikTok" },
  { icon: Youtube, href: "https://www.youtube.com/@OP2Pai", label: "YouTube" },
  { icon: Linkedin, href: "https://www.linkedin.com/company/op2p/", label: "LinkedIn" },
];

export function Footer() {
  const { theme } = useTheme();
  const currentLogo = theme === "dark" ? logoAsset.url : logoColorAsset.url;

  return (
    <footer className="border-t border-border bg-secondary">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <a href="/">
              <img src={currentLogo} alt="OP2P ⊶ CRM e Agentes de IA" className="h-[52px] w-auto mb-6" loading="lazy" />
            </a>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm leading-relaxed">
              WhatsApp e Instagram reunidos em um CRM com Agentes de IA para atender, qualificar e vender.
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail className="w-4 h-4" />
                contato@op2p.ai
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Phone className="w-4 h-4" />
                (11) 96324-2299
              </div>
            </div>

            <div className="mt-8">
              <Link
                to="/agenda"
                aria-label="Agende uma reunião com a OP2P"
                className="inline-flex items-center gap-2 h-11 px-4 whitespace-nowrap rounded-full bg-background border border-border text-sm font-semibold text-foreground hover:border-primary hover:text-primary transition-all"
              >
                <CalendarDays className="w-4 h-4" />
                Agende uma reunião
              </Link>
            </div>

            <div className="flex gap-2 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full bg-background flex items-center justify-center text-muted-foreground hover:text-primary border border-border hover:border-primary/30 transition-all"
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>

            
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-semibold mb-5 text-sm uppercase tracking-wide text-foreground">Navegação</h4>
            <ul className="space-y-3">
              {footerLinks.navigation.map((link) => (
                <li key={link.name}>
                  <HashLink to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {link.name}
                  </HashLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="font-semibold mb-5 text-sm uppercase tracking-wide text-foreground">Soluções</h4>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.name}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <HashLink to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                      {link.name}
                    </HashLink>
                  )}
                </li>
              ))}
            </ul>
          </div>


          {/* Company */}
          <div>
            <h4 className="font-semibold mb-5 text-sm uppercase tracking-wide text-foreground">Empresa</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <HashLink to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {link.name}
                  </HashLink>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-10 border-t border-border">
          <h4 className="font-semibold mb-5 text-sm uppercase tracking-wide text-foreground">Agentes por segmento</h4>
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-3">
            {footerLinks.segments.map((link) => (
              <li key={link.href}>
                <HashLink to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {link.name}
                </HashLink>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-14 pt-10 border-t border-border">
          <h4 className="font-semibold mb-5 text-sm uppercase tracking-wide text-foreground">Integrações do CRM</h4>
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-3">
            {integrationFooterLinks.map((link) => (
              <li key={link.href}>
                <HashLink to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {link.name}
                </HashLink>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom */}
        <div className="mt-16 pb-20 md:pb-0 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-sm text-muted-foreground text-center md:text-left">
            <p>© 2026 OP2P ⊶ CRM e Agentes de IA</p>
            <p className="mt-1">
              Publicidade e marketing digital por{" "}
              <a href="https://who6.com.br" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                WHO6 MKT
              </a>
            </p>
          </div>
          <PreferredSourceButton />
        </div>
      </div>
    </footer>
  );
}
